import axios from 'axios';

/**
 * Resolve API base URL. When deployed on HTTPS, use HTTPS for the API to avoid
 * mixed-content blocking (browsers block HTTP requests from HTTPS pages).
 */
function getApiBaseUrl() {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl) {
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && envUrl.startsWith('http://')) {
      return envUrl.replace(/^http:\/\//, 'https://');
    }
    return envUrl;
  }
  if (typeof window !== 'undefined') {
    if (window.location.protocol === 'https:') {
      return `${window.location.origin}/api`;
    }
    return 'http://localhost:5000/api';
  }
  return 'http://localhost:5000/api';
}

const API_BASE_URL = 'https://admtm.ggw.ly/api'; // getApiBaseUrl();

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const message = error.response.data?.message || error.response.data?.error || 'An error occurred';
      return Promise.reject(new Error(message));
    } else if (error.request) {
      return Promise.reject(new Error('Cannot connect to server'));
    }
    return Promise.reject(error);
  }
);

// Auth Service
export const authService = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    const { token, user } = response.data.data;
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
    }
    return response.data;
  },

  logout: async () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  isAuthenticated: async () => {
    return !!localStorage.getItem('token');
  },

  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// User Service
export const userService = {
  getUsers: async (filters = {}) => {
    const response = await api.get('/users', { params: filters });
    return response.data;
  },

  assignCredit: async (userId, credit) => {
    const response = await api.put(`/users/${userId}/credit`, { credit });
    return response.data;
  },

  addCredit: async (userId, amount) => {
    const response = await api.put(`/users/${userId}/add-credit`, { amount });
    return response.data;
  },

  updateRole: async (userId, role) => {
    const response = await api.put(`/users/${userId}/role`, { role });
    return response.data;
  },

  resetPassword: async (userId, newPassword) => {
    const response = await api.put(`/users/${userId}`, { password: newPassword });
    return response.data;
  },

  updateUser: async (userId, userData) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  deleteUser: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },

  toggleUserStatus: async (userId, is_active) => {
    const response = await api.put(`/users/${userId}`, { is_active });
    return response.data;
  },
};

// Team Service
export const teamService = {
  getTeams: async () => {
    const response = await api.get('/teams');
    return response.data;
  },

  createTeam: async (teamData) => {
    const response = await api.post('/teams', teamData);
    return response.data;
  },

  addMember: async (teamId, userId) => {
    const response = await api.post(`/teams/${teamId}/add-member`, { userId });
    return response.data;
  },

  removeMember: async (teamId, userId) => {
    const response = await api.post(`/teams/${teamId}/remove-member`, { userId });
    return response.data;
  },

  deleteTeam: async (teamId) => {
    const response = await api.delete(`/teams/${teamId}`);
    return response.data;
  },

  updateTeam: async (teamId, teamData) => {
    const response = await api.put(`/teams/${teamId}`, teamData);
    return response.data;
  },
};

// Installation Service
export const installationService = {
  getInstallations: async (filters = {}) => {
    const response = await api.get('/installations', { params: filters });
    return response.data;
  },

  getKPIStats: async () => {
    const response = await api.get('/installations/stats/kpi');
    return response.data;
  },

  updateInstallation: async (installationId, installationData) => {
    const response = await api.put(`/installations/${installationId}`, installationData);
    return response.data;
  },

  deleteInstallation: async (installationId) => {
    const response = await api.delete(`/installations/${installationId}`);
    return response.data;
  },
};

// Installation Item Service
export const installationItemService = {
  getItems: async (filters = {}) => {
    const response = await api.get('/installation-items', { params: filters });
    return response.data;
  },

  getItem: async (itemId) => {
    const response = await api.get(`/installation-items/${itemId}`);
    return response.data;
  },

  createItem: async (itemData) => {
    const response = await api.post('/installation-items', itemData);
    return response.data;
  },

  updateItem: async (itemId, itemData) => {
    const response = await api.put(`/installation-items/${itemId}`, itemData);
    return response.data;
  },

  deleteItem: async (itemId) => {
    const response = await api.delete(`/installation-items/${itemId}`);
    return response.data;
  },
};

// Installation Type Service (dynamic installation types)
export const installationTypeService = {
  getTypes: async () => {
    const response = await api.get('/installation-types');
    return response.data;
  },
  getAllTypes: async () => {
    const response = await api.get('/installation-types/all');
    return response.data;
  },
  getType: async (id) => {
    const response = await api.get(`/installation-types/${id}`);
    return response.data;
  },
  createType: async (data) => {
    const response = await api.post('/installation-types', data);
    return response.data;
  },
  updateType: async (id, data) => {
    const response = await api.put(`/installation-types/${id}`, data);
    return response.data;
  },
  deleteType: async (id) => {
    const response = await api.delete(`/installation-types/${id}`);
    return response.data;
  },
};

// Installation Device Service
export const installationDeviceService = {
  getDevices: async (filters = {}) => {
    const response = await api.get('/installation-devices', { params: filters });
    return response.data;
  },

  getDevice: async (deviceId) => {
    const response = await api.get(`/installation-devices/${deviceId}`);
    return response.data;
  },

  createDevice: async (deviceData) => {
    const response = await api.post('/installation-devices', deviceData);
    return response.data;
  },

  updateDevice: async (deviceId, deviceData) => {
    const response = await api.put(`/installation-devices/${deviceId}`, deviceData);
    return response.data;
  },

  deleteDevice: async (deviceId) => {
    const response = await api.delete(`/installation-devices/${deviceId}`);
    return response.data;
  },

  /**
   * Bulk import installation devices (e.g. from Excel).
   * @param {Array<{ city, area, barcode, status? }>} devices
   * @returns {Promise<{ success, data: { created, skipped, errorsCount, ... } }>}
   */
  importDevices: async (devices) => {
    const response = await api.post('/installation-devices/import', { devices });
    return response.data;
  },
};

// Warehouse Item Service
export const warehouseItemService = {
  getWarehouseItems: async () => {
    const response = await api.get('/warehouse-items');
    return response.data;
  },

  getWarehouseItem: async (itemId) => {
    const response = await api.get(`/warehouse-items/${itemId}`);
    return response.data;
  },

  createWarehouseItem: async (itemData) => {
    const response = await api.post('/warehouse-items', itemData);
    return response.data;
  },

  updateWarehouseItem: async (itemId, itemData) => {
    const response = await api.put(`/warehouse-items/${itemId}`, itemData);
    return response.data;
  },

  deleteWarehouseItem: async (itemId) => {
    const response = await api.delete(`/warehouse-items/${itemId}`);
    return response.data;
  },

  giveToTeam: async (itemId, teamData) => {
    const response = await api.post(`/warehouse-items/${itemId}/give-to-team`, teamData);
    return response.data;
  },

  getTeamAssignments: async (teamId = null) => {
    const url = teamId 
      ? `/warehouse-items/team-assignments/${teamId}`
      : '/warehouse-items/team-assignments';
    const response = await api.get(url);
    return response.data;
  },
};

export default api;

